import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useParams } from 'react-router';

import { useVendorById } from '@/api/vendor';
import Error404 from '@/components/errors/Error404';
import { InstantSearch, InstantSearchSortOrder, type QueryState } from '@/components/InstantSearch';
import { Page, PageDateRangeFilter, PageFilterProvider } from '@/components/page';
import { ReturnToLink } from '@/components/ReturnToLink';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { ROMSChart } from '@/features/charts/ROMSChart';
import VisitEfficiencyDonutChart from '@/features/charts/VisitEfficiencyDonutChart';
import { TrafficDetailKPIMetrics } from '@/features/TrafficDetailKPIMetrics';
import { ROUTES } from '@/router';

import VendorCampaignTable from './VendorCampaignTable';
import VendorDetailActions from './VendorDetailActions';
import VendorDirectViewThroughSankey from './VendorDirectViewThroughSankey';
import VendorOpportunityInfluenceChartTable from './VendorOpportunityInfluenceChartTable';
import VendorTagList from './VendorTagList';
import VendorWeeklyVisitsChart from './VendorWeeklyVisitsChart';

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  size: 20,
  sortBy: 'visits',
  sortOrder: InstantSearchSortOrder.DESC,
};

const VendorDetail = () => {
  const { vendor: vendorId } = useParams();

  const { vendor, isLoading } = useVendorById(vendorId);

  return (
    <Page
      title={t`Vendors`}
      pageName={vendor && `${vendor?.channel?.name} - ${vendor?.name}`}
      exampleAIQuestions={exampleAIQuestions('vendor')}
    >
      <div className="flex w-full flex-col gap-6">
        <div className="flex items-start justify-between gap-8">
          <ReturnToLink route={ROUTES.vendors.path} routeName={<Trans>Vendors</Trans>} />
          <VendorTagList vendors={vendor ? [vendor] : []} />
        </div>
        {!isLoading && vendor == null ? (
          <Error404 title={<Trans>The vendor you're looking for does not exist</Trans>} />
        ) : (
          <PageFilterProvider>
            <div className="flex justify-between">
              <PageDateRangeFilter />
              <VendorDetailActions vendorId={vendorId!} />
            </div>
            <TrafficDetailKPIMetrics
              objectType="vendor"
              vendorIds={vendorId ? [vendorId] : undefined}
              isLoading={isLoading}
            />
            <VendorWeeklyVisitsChart vendorId={vendor?.id} isLoading={isLoading} />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div>
                {vendorId === 'vndr_direct' ? (
                  <VendorDirectViewThroughSankey isLoading={isLoading} />
                ) : (
                  <ROMSChart
                    objectName={vendor ? `${vendor.name} - ${vendor.channel?.name}` : ''}
                    vendorIds={vendorId ? [vendorId] : undefined}
                    isLoading={isLoading}
                  />
                )}
              </div>
              <div>
                <VisitEfficiencyDonutChart vendorId={vendorId} isLoading={isLoading} />
              </div>
            </div>
            <InstantSearch prefix="c" defaultQueryState={DEFAULT_QUERY_STATE}>
              <VendorCampaignTable vendorId={vendor?.id} vendor={vendor} isLoading={isLoading} />
            </InstantSearch>
            <VendorOpportunityInfluenceChartTable
              vendorIds={vendorId ? [vendorId] : undefined}
              isLoading={isLoading}
              groupBy="VENDOR"
            />
          </PageFilterProvider>
        )}
      </div>
    </Page>
  );
};

export default VendorDetail;
